import * as React from "react";
import TeamsBaseComponent, {ITeamsBaseComponentState} from "msteams-react-base-component";
import {FilingLocation, DmsCaseInfo, PaginatedFilingLocations, DmsStorageType, LinkType} from "../../models";
import * as microsoftTeams from "@microsoft/teams-js";
import {get} from "../../api";
import {NetworkState} from "../ApiCallState";
import {TeamsApiService} from "../../services/teams/TeamsApiService";
import {TeamsGetTeamsChannelsRequest} from "../../services/teams/TeamsGetTeamsChannelsRequest";

export interface IDocumentsState extends ITeamsBaseComponentState {
    documents: FilingLocation[];
    documentsErrorMessage: string | null;
    dmsUrl: string;
    teamsId: string;
    tenantId: string;
    channelId: string;
    userId: string;
    rootNetworkState: NetworkState;
    teamsChannelNetworkState: NetworkState;
    rootContents: PaginatedFilingLocations;
    teamChannels: PaginatedFilingLocations;
    folderSearch: boolean | null;
}

export const DmsDocumentsContext: React.Context<DmsDocumentsContextProps> = React.createContext({
    documents: [],
    documentsErrorMessage: null,
    dmsUrl: "",
    rootNetworkState: NetworkState.Idle,
    teamsChannelNetworkState: NetworkState.Idle,
    teamsId: "",
    channelId: "",
    userId: "",
    tenantId: "",
    rootContents: null,
    teamChannels: null,
    theme: null,
    setParentState: () => {},
    fetchRoot: () => Promise.resolve(),
    folderSearch: false
});

export interface DmsDocumentsContextProps extends IDocumentsState {
    fetchRoot: () => Promise<void>;
    setParentState: (state: Partial<IDocumentsState>) => void;
}

export interface IDocumentsProps {
    caseInfo: DmsCaseInfo;
    linkType: LinkType;
    children: React.ReactElement;
}

export class DmsDocuments extends TeamsBaseComponent<IDocumentsProps, IDocumentsState> {
    teamsApiService = new TeamsApiService();

    constructor(props: IDocumentsProps) {
        super(props);
        this.state = {
            theme: null,
            documents: [],
            documentsErrorMessage: null,
            dmsUrl: "",
            rootNetworkState: NetworkState.Idle,
            teamsChannelNetworkState: NetworkState.Idle,
            teamsId: this.getQueryVariable("teamsId"),
            channelId: this.getQueryVariable("channelId"),
            userId: this.getQueryVariable("userId"),
            tenantId: this.getQueryVariable("tenantId"),
            rootContents: null,
            teamChannels: null,
            folderSearch: null
        };
    }

    public async componentWillMount(): Promise<void> {
        microsoftTeams.initialize();
        if (this.props.caseInfo.StorageType == DmsStorageType.NetDocuments) {
            const folderSearchString = window.localStorage.getItem("NetDocsFolderSearch");
            const folderSearch = folderSearchString === "true";

            await this.setState({folderSearch: folderSearch});
        }
        this.FetchRoot();
    }

    public FetchRoot = async () => {
        if (this.state.rootNetworkState == NetworkState.Loading) return;
        if (this.state.rootNetworkState != NetworkState.Success)
            this.setState({rootNetworkState: NetworkState.Loading});
        await Promise.all([this.getTeamsChannels(), this.getDmsUrl()]);
        if (this.state.rootNetworkState != NetworkState.Error) this.setState({rootNetworkState: NetworkState.Success});
    };

    async getDmsUrl() {
        const caseDetails = JSON.parse(this.props.caseInfo.CaseDetails);
        await get(
            "/api/teamsdmsintegration/GetDmsUrl?tenantId=" +
                this.state.tenantId +
                "&teamsId=" +
                this.state.teamsId +
                "&channelId=" +
                this.state.channelId +
                "&spHostKvPrefix=" +
                caseDetails.SpHostKVPrefix
        )
            .then((r) => {
                if (r.status.toString().startsWith("2")) {
                    return r.json();
                }
            })
            .then((r) => {
                const dmsUrl = r;
                this.setState({dmsUrl: dmsUrl});
            });
    }

    trimErrorMessage = (message: string) => {
        message = message.toString();
        // remove 'Error: ' from the string
        if (message.startsWith("Error: ")) {
            return message.slice(7);
        }
        return message;
    };

    private getTeamsChannels() {
        this.setState({teamsChannelNetworkState: NetworkState.Loading}); // this loading only blocks Teams channel selection for checkout
        const request: TeamsGetTeamsChannelsRequest = {
            channelId: this.state.channelId,
            externalTeamId: null,
            recentDocs: false,
            teamId: this.state.teamsId,
            tenantId: this.state.tenantId,
            userId: this.state.userId,
        };

        this.teamsApiService.getTeamsChannels(
            request,
            "Teams channels retrieval failed.",
            (response) =>
                this.setState({
                    teamChannels: {Page: response as FilingLocation[], PagingToken: null, Errors: null},
                    teamsChannelNetworkState: NetworkState.Success,
                }),
            (_error) => this.setState({teamsChannelNetworkState: NetworkState.Error})
        );
    }

    private FilteredDocuments() {
        if (this.state.rootContents == null) return null;
        return {
            ...this.state.rootContents,
            Page: this.state.rootContents.Page.filter(
                (p) => this.state.folderSearch == null || p.IsFile != this.state.folderSearch || this.props.linkType == LinkType.Folder // Don't remove files from root results if linked to a folder
            ),
        };
    }

    public render() {
        return (
            <div>
                <DmsDocumentsContext.Provider
                    value={{
                        ...this.state,
                        rootContents: this.FilteredDocuments(),
                        setParentState: (state: Pick<IDocumentsState, keyof IDocumentsState>) => this.setState(state),
                        fetchRoot: () => this.FetchRoot(),
                    }}
                >
                    {this.props.children}
                </DmsDocumentsContext.Provider>
            </div>
        );
    }
}

DmsDocuments.contextType = DmsDocumentsContext;
